import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../firebase';
import { ref, uploadBytes } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

// Utility: Resize and compress image
const resizeAndCompress = (file, maxWidth = 800, maxHeight = 600, quality = 0.7) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => (img.src = e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ratio = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error("Compression failed")),
        'image/jpeg',
        quality
      );
    };

    img.onerror = reject;
  });

export default function AdminScheduler() {
  const [date, setDate] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [message, setMessage] = useState('');
  const [word, setWord] = useState('');
  const [shuffled, setShuffled] = useState('');
  const [definition, setDefinition] = useState('');
  const [sentence, setSentence] = useState('');
  const [quote, setQuote] = useState('');
  const [status, setStatus] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.currentUser) {
      navigate('/admin-login');
    }
  }, [navigate]);

  const handlePuzzleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !imageFile || !message) {
      setStatus('🧩 All puzzle fields are required.');
      return;
    }

    setStatus('⏳ Compressing image...');
    let blob;
    try {
      blob = await resizeAndCompress(imageFile);
    } catch (err) {
      console.error(err);
      return setStatus('❌ Image compression failed.');
    }

    if (blob.size > 2 * 1024 * 1024) {
      return setStatus(`❌ Still too large (${(blob.size >> 20).toFixed(1)} MB). Try a smaller file.`);
    }

    try {
      const filename = `${date}__${imageFile.name}`;
      const filePath = `pod-images/${filename}`;
      const imageRef = ref(storage, filePath);
      await uploadBytes(imageRef, blob);
      await setDoc(doc(db, 'puzzleSchedule', date), {
        imagePath: filePath,
        message: message,
      });
      setStatus(`✅ Puzzle scheduled for ${date}`);
      setImageFile(null);
      setMessage('');
    } catch (err) {
      console.error(err);
      setStatus('❌ Failed to save puzzle.');
    }
  };

  const handleWordSubmit = async (e) => {
    e.preventDefault();
    if (!date || !word || !shuffled || !definition || !sentence || !quote) {
      return setStatus('🔤 All word fields are required.');
    }

    try {
      await setDoc(doc(db, 'wordSchedule', date), {
        word: word.toUpperCase(),
        shuffled: shuffled.toUpperCase(),
        definition,
        sentence,
        quote,
      });
      setStatus(`✅ Word scheduled for ${date}`);
      setWord('');
      setShuffled('');
      setDefinition('');
      setSentence('');
      setQuote('');
    } catch (err) {
      console.error(err);
      setStatus('❌ Failed to save word.');
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Admin Scheduler</h2>
      {status && <p style={styles.status}>{status}</p>}

      <label>
        Schedule Date:
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={styles.input}
          required
        />
      </label>

      {/* Puzzle Scheduler */}
      <form onSubmit={handlePuzzleSubmit} style={styles.form}>
        <h3>📷 Schedule Daily Puzzle</h3>
        <label>
          Upload Image:
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files[0])}
            style={styles.input}
          />
        </label>
        <label>
          Puzzle Message:
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            style={styles.textarea}
          />
        </label>
        <button type="submit" style={styles.button}>Save Puzzle</button>
      </form>

      {/* Word Scheduler */}
      <form onSubmit={handleWordSubmit} style={styles.form}>
        <h3>🔤 Schedule Daily Word</h3>
        <label>
          Word:
          <input
            type="text"
            value={word}
            onChange={(e) => setWord(e.target.value)}
            style={styles.input}
          />
        </label>
        <label>
          Shuffled:
          <input
            type="text"
            value={shuffled}
            onChange={(e) => setShuffled(e.target.value)}
            style={styles.input}
          />
        </label>
        <label>
          Definition:
          <textarea
            value={definition}
            onChange={(e) => setDefinition(e.target.value)}
            rows={2}
            style={styles.textarea}
          />
        </label>
        <label>
          Sentence:
          <textarea
            value={sentence}
            onChange={(e) => setSentence(e.target.value)}
            rows={2}
            style={styles.textarea}
          />
        </label>
        <label>
          Quote:
          <textarea
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            rows={2}
            style={styles.textarea}
          />
        </label>
        <button type="submit" style={styles.button}>Save Word</button>
      </form>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '600px',
    margin: '4rem auto',
    padding: '2rem',
    borderRadius: '8px',
    backgroundColor: '#fff',
    boxShadow: '0 0 20px rgba(0,0,0,0.1)'
  },
  heading: {
    textAlign: 'center',
    marginBottom: '1.5rem'
  },
  status: {
    marginBottom: '1rem',
    color: '#2563EB',
    fontWeight: 'bold'
  },
  form: {
    marginTop: '2rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  input: {
    padding: '0.5rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    width: '100%'
  },
  textarea: {
    padding: '0.5rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    width: '100%'
  },
  button: {
    padding: '0.75rem',
    fontSize: '1rem',
    backgroundColor: '#2563EB',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  }
};
